import React, { useEffect, useRef } from "react";
import { Box, Text, Flex } from "@chakra-ui/react";
import { motion } from "framer-motion";

/** Chakra <Box> that can take framer-motion props. */
export const MotionBox = motion(Box);
export const MotionFlex = motion(Flex);

/**
 * Parallax — translates its children on the Y axis as the page scrolls, based
 * on the element's distance from the viewport centre. `speed` sets how far it
 * drifts (positive = moves opposite to scroll, giving depth). Uses a single
 * rAF-batched scroll listener and only touches `transform`, so it stays cheap.
 */
export function Parallax({ children, speed = 0.12, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = null;
    const update = () => {
      raf = null;
      const rect = el.getBoundingClientRect();
      const fromCenter = rect.top + rect.height / 2 - window.innerHeight / 2;
      el.style.transform = `translate3d(0, ${(-fromCenter * speed).toFixed(1)}px, 0)`;
    };
    const onScroll = () => {
      if (raf == null) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, [speed]);

  return (
    <Box ref={ref} willChange="transform" {...rest}>
      {children}
    </Box>
  );
}

/**
 * Reveal — fades + slides its children in the first time they scroll into view.
 * `dir` controls the entry direction, `delay` staggers siblings.
 */
export function Reveal({ children, delay = 0, dir = "up", ...rest }) {
  const offset = {
    up: { y: 44, x: 0 },
    down: { y: -44, x: 0 },
    left: { x: 64, y: 0 },
    right: { x: -64, y: 0 },
  }[dir];

  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.96, filter: "blur(6px)", ...offset }}
      whileInView={{ opacity: 1, x: 0, y: 0, scale: 1, filter: "blur(0px)" }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </MotionBox>
  );
}

/**
 * ChapterHeading — the little "CHAPTER 01" kicker + a large title that
 * frames every section as the next page of the story.
 */
export function ChapterHeading({ chapter, title, subtitle, compact = false }) {
  return (
    <Parallax
      speed={0.08}
      mb={compact ? { base: "22px", md: "30px" } : { base: "40px", md: "64px" }}
      textAlign="center"
    >
      <Reveal>
        <Flex align="center" justify="center" gap="14px" mb="14px">
          <Box
            h="1px"
            w={{ base: "28px", md: "48px" }}
            bgGradient="linear(to-r, transparent, #60A5FA)"
          />
          <Text
            fontSize={{ base: "xs", md: "sm" }}
            letterSpacing="0.35em"
            fontWeight="bold"
            color="#60A5FA"
            textTransform="uppercase"
          >
            {chapter}
          </Text>
          <Box
            h="1px"
            w={{ base: "28px", md: "48px" }}
            bgGradient="linear(to-l, transparent, #60A5FA)"
          />
        </Flex>
      </Reveal>
      <Reveal delay={0.1}>
        <Text
          as="h2"
          fontSize={
            compact
              ? { base: "26px", md: "36px", lg: "42px" }
              : { base: "30px", md: "46px", lg: "54px" }
          }
          fontWeight="800"
          lineHeight="1.1"
          bgGradient="linear(to-b, #ffffff, #9aa4bd)"
          bgClip="text"
        >
          {title}
        </Text>
      </Reveal>
      {subtitle && (
        <Reveal delay={0.2}>
          <Text
            mt={compact ? "10px" : "16px"}
            maxW="620px"
            mx="auto"
            fontSize={{ base: "sm", md: "md" }}
            color="#98a2b8"
            lineHeight="1.8"
          >
            {subtitle}
          </Text>
        </Reveal>
      )}
    </Parallax>
  );
}

/**
 * Steel glass panel (the 30% surface tone of the 60-30-10 palette).
 * Semi-transparent so the blurred aurora glows softly through it, which reads
 * as frosted glass WITHOUT a per-card backdrop-filter — the thing that made
 * the page sluggish. Opacity stays high enough to guarantee text contrast.
 */
export function glass(extra = {}) {
  return {
    bg: "rgba(28, 37, 52, 0.68)",
    border: "1px solid rgba(96, 165, 250, 0.16)",
    borderRadius: "18px",
    boxShadow: "0 12px 40px rgba(4, 8, 16, 0.5)",
    ...extra,
  };
}
